import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  Stack,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Skeleton,
  CircularProgress,
  FormControlLabel,
  Switch
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Clear as ClearIcon,
  MoreVert as MoreVertIcon,
  EditOutlined as EditIcon,
  DeleteOutlined as DeleteIcon,
  FolderOutlined as FolderIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Folders } from '@phosphor-icons/react';
import { useHeaderContext } from '@/contexts/HeaderContext';
import { useNavigate, useParams } from 'react-router-dom';
import { format, parseISO, isValid as isValidDate } from 'date-fns';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppUser } from '@/contexts/AppUserProvider';
import {
  getCollectionsByCompany,
  createCollection,
  updateCollection,
  deleteCollection
} from '@/services/sessionCollectionService';
import { SessionCollection } from '@/types/sessionCollectionTypes';
import { AttioTextField } from '@/components/AttioForm';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { EmptyState } from '@/components/EmptyState';
import { useToast } from '@/components/Toast';

const formatDateDisplay = (
  dateString?: string | null,
  dateFormat = 'PPp'
): string => {
  if (!dateString) return 'N/A';
  const date = parseISO(dateString);
  return isValidDate(date) ? format(date, dateFormat) : 'Invalid Date';
};

interface CollectionDialogData {
  id?: string;
  title: string;
  description: string;
  referenceNumber: string;
  exposeToCustomerAgent: boolean;
}

export const CollectionsPage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { setHeaderText, setMenuActions } = useHeaderContext();
  const navigate = useNavigate();
  const { id: collectionIdParam } = useParams<{ id: string }>();
  const { getAccessTokenSilently, getAccessTokenWithPopup } = useAuth0();
  const { appUser, selectedUserRole } = useAppUser();
  const toast = useToast();

  // State
  const [collections, setCollections] = useState<SessionCollection[]>([]);
  const [filteredCollections, setFilteredCollections] = useState<
    SessionCollection[]
  >([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCollection, setEditingCollection] =
    useState<CollectionDialogData | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCollection, setSelectedCollection] =
    useState<SessionCollection | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<SessionCollection | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Load collections
  const loadCollections = useCallback(async () => {
    if (!selectedUserRole || !appUser) return;

    setIsLoading(true);
    try {
      const data = await getCollectionsByCompany(
        selectedUserRole.companyId,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup,
        1,
        100,
        searchTerm || undefined
      );
      setCollections(data);
      setFilteredCollections(data);
    } catch (error) {
      console.error('Error loading collections:', error);
      toast.showError('Failed to load folders. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedUserRole,
    appUser,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
    searchTerm,
    toast
  ]);

  useEffect(() => {
    loadCollections();
  }, [loadCollections]);

  // Filter collections based on search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredCollections(collections);
    } else {
      const lowerSearch = searchTerm.toLowerCase();
      setFilteredCollections(
        collections.filter(
          (c) =>
            c.title?.toLowerCase().includes(lowerSearch) ||
            c.description?.toLowerCase().includes(lowerSearch) ||
            c.referenceNumber?.toLowerCase().includes(lowerSearch)
        )
      );
    }
  }, [searchTerm, collections]);

  const handleCreateNew = useCallback(() => {
    setEditingCollection({
      title: '',
      description: '',
      referenceNumber: '',
      exposeToCustomerAgent: false
    });
    setDialogOpen(true);
  }, []);

  // Set header
  useEffect(() => {
    setHeaderText(
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Folders size={16} weight="regular" />
        <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
          Folders
        </Typography>
      </Box>
    );
    setMenuActions(
      isMobile ? (
        <IconButton
          onClick={handleCreateNew}
          sx={{
            backgroundColor: '#e6f226',
            color: '#030766',
            borderRadius: '6px',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: '#d4e022'
            }
          }}
        >
          <AddIcon sx={{ fontSize: 18 }} />
        </IconButton>
      ) : (
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateNew}
          sx={{
            borderRadius: '6px',
            textTransform: 'none',
            fontWeight: 600,
            px: 2.5,
            backgroundColor: '#e6f226',
            color: '#030766',
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: '#d4e022',
              boxShadow: 'none'
            }
          }}
        >
          New Folder
        </Button>
      )
    );
    return () => {
      setHeaderText('');
      setMenuActions([]);
    };
  }, [setHeaderText, setMenuActions, handleCreateNew, theme.palette.mode, isMobile]);

  const handleEdit = (collection: SessionCollection) => {
    setEditingCollection({
      id: collection.id,
      title: collection.title,
      description: collection.description || '',
      referenceNumber: collection.referenceNumber || '',
      exposeToCustomerAgent: collection.exposeToCustomerAgent
    });
    setDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDeleteClick = (collection: SessionCollection) => {
    setCollectionToDelete(collection);
    setDeleteDialogOpen(true);
    setMenuAnchorEl(null);
  };

  const handleDeleteConfirm = async () => {
    if (!appUser || !collectionToDelete) return;

    setIsDeleting(true);
    try {
      await deleteCollection(
        collectionToDelete.id,
        appUser.id,
        getAccessTokenSilently,
        getAccessTokenWithPopup
      );
      await loadCollections();
      setDeleteDialogOpen(false);
      setCollectionToDelete(null);
      toast.showSuccess('Folder deleted');
    } catch (error) {
      console.error('Error deleting collection:', error);
      toast.showError('Failed to delete folder');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCollectionToDelete(null);
  };

  const handleSaveCollection = async () => {
    if (!editingCollection || !selectedUserRole || !appUser) return;

    const isEditing = !!editingCollection.id;
    setIsSaving(true);
    try {
      if (isEditing) {
        // Update existing
        await updateCollection(
          editingCollection.id!,
          {
            title: editingCollection.title,
            description: editingCollection.description || null,
            referenceNumber: editingCollection.referenceNumber || null,
            exposeToCustomerAgent: editingCollection.exposeToCustomerAgent
          },
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      } else {
        // Create new
        await createCollection(
          {
            companyId: selectedUserRole.companyId,
            title: editingCollection.title,
            description: editingCollection.description || null,
            referenceNumber: editingCollection.referenceNumber || null,
            exposeToCustomerAgent: editingCollection.exposeToCustomerAgent
          },
          appUser.id,
          getAccessTokenSilently,
          getAccessTokenWithPopup
        );
      }
      await loadCollections();
      setDialogOpen(false);
      setEditingCollection(null);
      toast.showSuccess(isEditing ? 'Folder updated' : 'Folder created');
    } catch (error) {
      console.error('Error saving collection:', error);
      toast.showError('Failed to save folder');
    } finally {
      setIsSaving(false);
    }
  };

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    collection: SessionCollection
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedCollection(collection);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedCollection(null);
  };

  const handleViewCollection = (collection: SessionCollection) => {
    navigate(`/collections/${collection.id}`);
  };

  // If showing detail view (collectionIdParam exists), redirect to detail page component
  // For now, just show list view
  if (collectionIdParam) {
    // TODO: Create CollectionDetailPage component
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom>
          Collection Detail View
        </Typography>
        <Typography color="text.secondary">
          Collection ID: {collectionIdParam}
        </Typography>
        <Button onClick={() => navigate('/collections')} sx={{ mt: 2 }}>
          Back to Folders
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        backgroundColor: theme.palette.background.paper
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          position: 'sticky',
          top: isMobile ? '56px' : '64px',
          zIndex: 10,
          px: isMobile ? 2 : 3,
          pt: 2,
          pb: 0,
          background: theme.palette.background.paper
        }}
      >
        {/* Search Bar */}
        <Box sx={{ mb: 2 }}>
          <TextField
          fullWidth
          variant="outlined"
          placeholder="Search folders..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontSize: '13px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              height: 40,
              '&:hover': {
                borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db'
              },
              '&.Mui-focused': {
                borderColor: '#6b7280',
                boxShadow: 'none'
              }
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none'
            }
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchTerm('')}>
                  <ClearIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        </Box>
      </Box>

      {/* Main Content */}
      <Box
        sx={{
          px: isMobile ? 2 : 3,
          pt: 0,
          pb: 3,
          background: theme.palette.background.paper
        }}
      >
        {/* Collections List */}
        {isLoading ? (
          <Stack spacing={1}>
            {[1, 2, 3].map((i) => (
              <Card
                key={i}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  p: 2,
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5
                }}
              >
                <Skeleton 
                  variant="rounded" 
                  width={32} 
                  height={32} 
                  sx={{ 
                    borderRadius: '4px',
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255,255,255,0.05)' 
                      : 'rgba(0,0,0,0.06)'
                  }} 
                />
                <Box sx={{ flex: 1 }}>
                  <Skeleton 
                    width="45%" 
                    height={20} 
                    sx={{ 
                      mb: 0.5,
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(0,0,0,0.06)'
                    }} 
                  />
                  <Skeleton 
                    width="60%" 
                    height={16} 
                    sx={{ 
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.05)' 
                        : 'rgba(0,0,0,0.06)'
                    }} 
                  />
                </Box>
              </Card>
            ))}
          </Stack>
        ) : filteredCollections.length > 0 ? (
          <Stack spacing={1}>
            {filteredCollections.map((collection, index) => (
              <Card
                key={collection.id}
                onClick={() => handleViewCollection(collection)}
                sx={{
                  background: theme.palette.background.paper,
                  borderRadius: '6px',
                  boxShadow: 'none',
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'hidden',
                  transition: 'all 0.15s ease',
                  cursor: 'pointer',
                  opacity: 0,
                  animation: 'fadeSlideIn 0.3s ease forwards',
                  animationDelay: `${index * 30}ms`,
                  '@keyframes fadeSlideIn': {
                    from: { opacity: 0, transform: 'translateY(8px)' },
                    to: { opacity: 1, transform: 'translateY(0)' }
                  },
                  '&:hover': {
                    borderColor: theme.palette.mode === 'dark' ? '#3f3f46' : '#d1d5db',
                    backgroundColor:
                      theme.palette.mode === 'dark'
                        ? 'rgba(255, 255, 255, 0.02)'
                        : '#fafafa'
                  },
                  '&:active': {
                    transform: 'scale(0.98)',
                    transition: 'transform 0.1s ease'
                  }
                }}
              >
                <Box
                  sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  {/* Left Side - Collection Info */}
                  <Box
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'flex-start', 
                      gap: 1.5,
                      flex: 1,
                      minWidth: 0
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: theme.palette.mode === 'dark' 
                          ? 'rgba(255,255,255,0.05)' 
                          : '#f3f4f6',
                        color: '#6b7280',
                        flexShrink: 0
                      }}
                    >
                      <Folders size={14} weight="regular" />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      {/* Title Row */}
                      <Typography
                        sx={{
                          fontSize: '13px',
                          fontWeight: 600,
                          color: theme.palette.text.primary,
                          textAlign: 'left',
                          mb: 0.5,
                          lineHeight: 1.4,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {collection.title}
                      </Typography>

                      {/* Description and Metadata Row */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          flexWrap: 'wrap'
                        }}
                      >
                        {collection.referenceNumber && (
                          <Chip
                            label={collection.referenceNumber}
                            size="small"
                            sx={{
                              fontSize: '11px',
                              height: '20px',
                              borderRadius: '4px',
                              backgroundColor: theme.palette.mode === 'dark' 
                                ? 'rgba(255,255,255,0.05)' 
                                : '#f3f4f6',
                              color: theme.palette.text.secondary,
                              border: 'none',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        )}
                        {collection.exposeToCustomerAgent && (
                          <Chip
                            label="Customer searchable"
                            size="small"
                            color="success"
                            variant="outlined"
                            sx={{
                              fontSize: '11px',
                              height: '20px',
                              borderRadius: '4px',
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        )}
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: theme.palette.text.secondary
                          }}
                        >
                          {collection.sessionCount} {collection.sessionCount === 1 ? 'recording' : 'recordings'} • {formatDateDisplay(collection.createdAt, 'MMM dd, yyyy')}
                        </Typography>
                      </Box>

                      {/* Description */}
                      {collection.description && (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: theme.palette.text.secondary,
                            mt: 1,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {collection.description}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Right Side - Actions */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, collection)}
                      sx={{
                        borderRadius: '6px',
                        color: theme.palette.text.secondary,
                        '&:hover': {
                          color: theme.palette.text.primary,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
                        }
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Card>
            ))}
          </Stack>
        ) : (
          <EmptyState
            icon={<Folders size={22} weight="regular" />}
            title={searchTerm ? 'No folders found' : 'No folders yet'}
            description={
              searchTerm
                ? 'Try adjusting your search terms'
                : 'Create your first folder to organize recordings together'
            }
          />
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() =>
            selectedCollection && handleViewCollection(selectedCollection)
          }
        >
          <ListItemIcon>
            <FolderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedCollection && handleEdit(selectedCollection)}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => selectedCollection && handleDeleteClick(selectedCollection)}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Folder"
        message={<>Are you sure you want to delete <strong>"{collectionToDelete?.title}"</strong>? This action cannot be undone.</>}
        confirmText="Delete"
        variant="danger"
        isLoading={isDeleting}
      />

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0, 0, 0, 0.6)' 
              : '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Typography 
            sx={{ 
              fontSize: '18px',
              fontWeight: 600,
              color: theme.palette.text.primary
            }}
          >
            {editingCollection?.id ? 'Edit Folder' : 'Create New Folder'}
          </Typography>
          <IconButton 
            onClick={() => setDialogOpen(false)}
            sx={{
              borderRadius: '6px',
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f3f4f6'
              }
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          <Box 
            sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1.5 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && editingCollection?.title.trim() && !isSaving) {
                e.preventDefault();
                handleSaveCollection();
              }
            }}
          >
            <AttioTextField
              label="Title"
              value={editingCollection?.title || ''}
              onChange={(e) =>
                setEditingCollection(
                  (prev) => prev && { ...prev, title: e.target.value }
                )
              }
              required
              autoFocus
              placeholder="e.g., Case #2024-001"
            />
            
            <AttioTextField
              label="Reference Number"
              value={editingCollection?.referenceNumber || ''}
              onChange={(e) =>
                setEditingCollection(
                  (prev) => prev && { ...prev, referenceNumber: e.target.value }
                )
              }
              placeholder="e.g., CASE-2024-001"
            />
            
            <AttioTextField
              label="Description"
              value={editingCollection?.description || ''}
              onChange={(e) =>
                setEditingCollection(
                  (prev) => prev && { ...prev, description: e.target.value }
                )
              }
              placeholder="Describe the purpose of this folder..."
            />

            <FormControlLabel
              control={
                <Switch
                  checked={editingCollection?.exposeToCustomerAgent ?? false}
                  onChange={(event) =>
                    setEditingCollection(
                      (prev) =>
                        prev && {
                          ...prev,
                          exposeToCustomerAgent: event.target.checked
                        }
                    )
                  }
                />
              }
              label="Customer searchable"
              sx={{
                alignSelf: 'flex-start',
                m: 0,
                '& .MuiFormControlLabel-label': {
                  fontSize: '14px',
                  fontWeight: 500
                }
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions 
          sx={{ 
            px: 3, 
            py: 2.5,
            borderTop: `1px solid ${theme.palette.divider}`,
            gap: 1.5
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            disabled={isSaving}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 500,
              fontSize: '14px',
              px: 2.5,
              color: theme.palette.text.secondary,
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f3f4f6'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveCollection}
            variant="contained"
            disabled={!editingCollection?.title.trim() || isSaving}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              borderRadius: '6px',
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '14px',
              px: 3,
              backgroundColor: theme.palette.mode === 'dark' ? '#3b82f6' : '#1A1A1A',
              color: 'white',
              '&:hover': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2563eb' : '#000'
              }
            }}
          >
            {isSaving ? 'Saving...' : editingCollection?.id ? 'Save Changes' : 'Create Folder'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
